import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { handlePossibleNonFieldError } from 'api/query-client';
import { t } from 'ui/authenticated/main/pages/journeys/traveller-journey-list/locale';

import {
  JourneyNotificationType,
  loadJourneyNotification,
  subscribeJourneyNotification,
  unsubscribeJourneyNotification,
} from './requests';

export const getJourneyNotificationKey = (journeyId?: string, type?: JourneyNotificationType) =>
  ['journey-notification', journeyId, type].filter(
    (value): value is string | JourneyNotificationType => Boolean(value),
  );

export const useJourneyNotification = (journeyShortId: string, type: JourneyNotificationType) => {
  return useQuery({
    queryKey: getJourneyNotificationKey(journeyShortId, type),
    queryFn: () => loadJourneyNotification(journeyShortId, type),
    enabled: Boolean(journeyShortId) && Boolean(type),
  });
};

export const useSubscribeJourneyNotification = (
  journeyShortId: string,
  type: JourneyNotificationType,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => subscribeJourneyNotification({ journeyShortId, type }),
    onSuccess: () => {
      queryClient.invalidateQueries(getJourneyNotificationKey(journeyShortId, type));
    },
    onError: (e: unknown) => {
      handlePossibleNonFieldError(e, t('notificationUpdateFailed'));
    },
  });
};

export const useUnsubscribeJourneyNotification = (
  journeyShortId: string,
  type: JourneyNotificationType,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unsubscribeJourneyNotification({ journeyShortId, type }),
    onSuccess: () => {
      queryClient.invalidateQueries(getJourneyNotificationKey(journeyShortId, type));
    },
    onError: (e: unknown) => {
      handlePossibleNonFieldError(e, t('notificationUpdateFailed'));
    },
  });
};
